/**
 * StepCanvas — vertical stack of StepCard components.
 *
 * This is a thin presentational shell; ownership of the step list, dirty
 * tracking, and DnD routing all live one level up in {@link VisualBuilder}.
 * Splitting it out keeps the builder shell readable.
 */

import { AddStepButton, StepCard } from './StepCard.js';
import type { ActionCatalogRecipient } from './ActionRow.js';
import type { VisualStep } from './serialize.js';

export interface StepCanvasProps {
  steps: readonly VisualStep[];
  readOnly: boolean;
  emailRecipients: readonly ActionCatalogRecipient[];
  phoneRecipients: readonly ActionCatalogRecipient[];
  onPatchStep: (key: string, next: VisualStep) => void;
  onRemoveStep: (key: string) => void;
  onAddStep: () => void;
}

export function StepCanvas({
  steps,
  readOnly,
  emailRecipients,
  phoneRecipients,
  onPatchStep,
  onRemoveStep,
  onAddStep,
}: StepCanvasProps) {
  return (
    <div className="flex flex-col gap-4 bg-brand-cream/40 p-4">
      {steps.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <p className="font-display text-sm font-semibold text-brand-navy-800">
            No steps yet
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Add your first step, then drag fields onto the condition row and
            actions onto the action row.
          </p>
          <div className="mt-3">
            <AddStepButton disabled={readOnly} onAdd={onAddStep} />
          </div>
        </div>
      )}

      {steps.map((step, idx) => (
        <StepCard
          key={step.key}
          step={step}
          index={idx}
          total={steps.length}
          readOnly={readOnly}
          emailRecipients={emailRecipients}
          phoneRecipients={phoneRecipients}
          onPatch={(next) => onPatchStep(step.key, next)}
          onRemove={() => onRemoveStep(step.key)}
        />
      ))}

      {steps.length > 0 && <AddStepButton disabled={readOnly} onAdd={onAddStep} />}
    </div>
  );
}
